/* D3 — sidebar contextuelle de System : quand l'utilisateur est DANS un System
   (Deal / Financial Markets / Governance), la sidebar montre la navigation de ce
   System + les capacités Platform partagées (les 6 piliers du hub, pas un
   fourre-tout). Shell uniquement : aucune page déplacée, aucune URL renommée,
   le gating serveur D1 reste la seule sécurité. */
import { Link, NavLink, useLocation } from "react-router-dom";
import { Activity, Bot, Database, GitBranch, Hexagon, MessageSquare, Network, Radio, Scale3d, ShieldCheck, Sparkles } from "lucide-react";
import { SYSTEM_DEFS, useSystems } from "@/lib/systems";

const PLATFORM_LINKS = [
  { to: "/app/radar", label: "Noam Radar", icon: Radio, module: "radar" },
  { to: "/app/ontologie", label: "Ontology", icon: Network },
  { to: "/app/intelligence", label: "Intelligence", icon: Sparkles },
  { to: "/app/decisions", label: "Decision Journal", icon: Scale3d },
  { to: "/app/workflows", label: "Workflow", icon: GitBranch },
  { to: "/app/real-validation", label: "Validation", icon: ShieldCheck },
  { to: "/app/process", label: "Process", icon: Activity },
  { to: "/app/settings/agents", label: "AI & Automation", icon: Bot },
  { to: "/app/users", label: "Trust & Security", icon: ShieldCheck },
  { to: "/app/chat-interne", label: "Communication", icon: MessageSquare },
  { to: "/app/documents", label: "Data & Documents", icon: Database },
];

export const SystemSidebar = ({ system, collapsed, allowedModules, onNavigate }) => {
  const def = SYSTEM_DEFS.find((d) => d.id === system);
  const location = useLocation();
  const { depth } = useSystems();
  if (!def) return null;
  // W17 Progressive depth : « Reveal complexity when it becomes relevant. »
  const sysDepth = depth?.[system] ?? 99;
  const items = def.nav.filter((it) =>
    (!allowedModules || !it.module || allowedModules.includes(it.module))
    && (it.min_depth == null || it.min_depth <= sysDepth));

  const isItemActive = (it) => {
    const [path] = it.to.split("?");
    if (location.pathname !== path && !location.pathname.startsWith(path + "/")) return false;
    if (it.tab) {
      const cur = new URLSearchParams(location.search).get("tab") || "screener";
      return cur === it.tab;
    }
    return true;
  };

  return (
    <div data-testid={`system-sidebar-${def.id}`}>
      {/* En-tête du System courant */}
      <div className={`mt-4 ${collapsed ? "mx-2" : "mx-4"} px-3 py-2.5 rounded-md border`}
        style={{ borderColor: `${def.accent}44`, background: `${def.accent}12` }}>
        <div className="flex items-center gap-2">
          <def.icon className="w-4 h-4 shrink-0" style={{ color: def.accent }} />
          {!collapsed && (
            <div className="min-w-0">
              <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-500">System</div>
              <div className="text-sm font-semibold text-white truncate" data-testid="system-sidebar-label">{def.label}</div>
            </div>
          )}
        </div>
      </div>
      <Link
        to="/app/hub"
        onClick={onNavigate}
        className={`${collapsed ? "mx-2 justify-center" : "mx-4"} mt-2 px-3 py-2 rounded-md flex items-center gap-2 text-xs text-[#B5D4F4]/70 border border-white/5 hover:bg-white/5 hover:text-white transition-colors`}
        data-testid="system-sidebar-back-hub"
        title="Hub NOAM"
      >
        <Hexagon className="w-3.5 h-3.5 text-[#378ADD] shrink-0" />
        {!collapsed && <span>Hub NOAM</span>}
      </Link>

      {/* Navigation du System */}
      <div className="mt-5">
        {!collapsed && (
          <div className="px-5 mb-2">
            <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase" style={{ color: def.accent }}>
              <def.icon className="w-3 h-3" />
              {def.label}
            </div>
          </div>
        )}
        {items.map((it, idx) => (
          <NavLink
            key={it.to}
            to={it.to}
            onClick={onNavigate}
            className={() => `noam-nav-item ${isItemActive(it) ? "active" : ""} ${collapsed ? "justify-center" : ""}`}
            title={collapsed ? it.label : ""}
            data-testid={`system-nav-${def.id}-${idx}`}
          >
            <it.icon className="w-4 h-4 opacity-80 shrink-0" />
            {!collapsed && <span className="flex-1">{it.label}</span>}
          </NavLink>
        ))}
      </div>

      {/* Platform partagée — capacités transverses uniquement (mêmes 6 piliers que le hub) */}
      <div className="mt-6">
        {!collapsed && (
          <div className="px-5 mb-2">
            <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#B5D4F4]/60">
              <Hexagon className="w-3 h-3" />
              Platform partagée
            </div>
          </div>
        )}
        {collapsed && <div className="h-px mx-3 my-3 bg-white/5" />}
        {PLATFORM_LINKS
          .filter((p) => !p.module || !allowedModules || allowedModules.includes(p.module))
          .map((p) => (
          <NavLink
            key={p.to}
            to={p.to}
            onClick={onNavigate}
            className={({ isActive }) => `noam-nav-item ${isActive ? "active" : ""} ${collapsed ? "justify-center" : ""}`}
            title={collapsed ? p.label : ""}
            data-testid={`system-platform-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            <p.icon className="w-4 h-4 opacity-60 shrink-0" />
            {!collapsed && <span className="flex-1 text-[#B5D4F4]/80">{p.label}</span>}
          </NavLink>
        ))}
      </div>
    </div>
  );
};

export default SystemSidebar;
